import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Features } from '../../assets/features/Features';
import { FaCookie, FaTimes } from 'react-icons/fa';

const ANON_KEY = 'consentAnonId';
const CONSENT_LOG_URL = 'https://wtjqwipbjbjicdnkpdiq.supabase.co/functions/v1/consent-log';
const CONSENT_SITE_KEY = 'bfd9e1d02c5dc7251f196a21ce538eff';
const BANNER_VERSION = 'v1';

function getAnonId() {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem(ANON_KEY, id);
    }
    return id;
}

// Central GDPR art. 7(1) evidence store — best-effort, the localStorage
// record stays the source of truth for the banner itself.
function logConsent(action, granted, lang) {
    try {
        fetch(CONSENT_LOG_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                siteKey: CONSENT_SITE_KEY,
                anonId: getAnonId(),
                action,
                categories: {
                    essential: true,
                    analytics: granted,
                    marketing: granted,
                    preferences: granted,
                },
                bannerVersion: BANNER_VERSION,
                lang: lang || 'pt',
            }),
            keepalive: true,
        }).catch(() => { /* best-effort */ });
    } catch (e) { /* ignore (no fetch/randomUUID) */ }
}

const CookieConsent = () => {
    const { language } = useLanguage();
    const [showConsent, setShowConsent] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Check if user has already consented
        const cookieConsent = localStorage.getItem('cookieConsent');
        
        if (!cookieConsent) {
            // Show consent bar after a short delay for better UX
            setTimeout(() => {
                setShowConsent(true);
                // Trigger animation after state update
                setTimeout(() => setIsVisible(true), 10);
            }, 1000);
        }

        // Footer "Definições de cookies": reopen showing the current choice,
        // so withdrawing consent is as easy as giving it (GDPR art. 7(3)).
        const reopen = () => {
            setShowConsent(true);
            setTimeout(() => setIsVisible(true), 10);
        };
        window.addEventListener('openCookieSettings', reopen);
        return () => window.removeEventListener('openCookieSettings', reopen);
    }, []);

    // Expire first-party tracking cookies on withdrawal (GA sets them with
    // domain=.example.com, so host and apex variants are both tried).
    const purgeTrackingCookies = () => {
        const doomed = /^(_ga|_ga_.*|_gid|_gat.*|_gcl_au|_gcl_aw|_gcl_dc|_gac_.*|_fbp|_fbc)$/;
        const host = window.location.hostname;
        const apex = '.' + host.replace(/^www\./, '');
        const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
        document.cookie.split(';').forEach((c) => {
            const name = c.split('=')[0].trim();
            if (!doomed.test(name)) return;
            document.cookie = `${name}=; ${expiry}`;
            document.cookie = `${name}=; ${expiry}; domain=${host}`;
            document.cookie = `${name}=; ${expiry}; domain=${apex}`;
        });
    };

    // Tell Google Consent Mode about the choice — the index.html default is
    // "denied", so GA/Ads tags stay cookieless until this fires with granted.
    // Pushed straight into dataLayer (NOT via window.gtag): the Firebase
    // Analytics SDK overwrites window.gtag with a wrapper that mangles
    // consent commands, so the update never reached Google through it.
    const applyGtagConsent = (granted) => {
        const state = granted ? 'granted' : 'denied';
        window.dataLayer = window.dataLayer || [];
        function gtagLocal() { window.dataLayer.push(arguments); }
        gtagLocal('consent', 'update', {
            ad_storage: state,
            ad_user_data: state,
            ad_personalization: state,
            analytics_storage: state,
            personalization_storage: state,
        });
    };

    const handleAccept = () => {
        localStorage.setItem('cookieConsent', 'accepted');
        applyGtagConsent(true);
        logConsent('accept_all', true, language);
        setIsVisible(false);
        setTimeout(() => setShowConsent(false), 300);
    };

    const handleDecline = () => {
        const wasAccepted = localStorage.getItem('cookieConsent') === 'accepted';
        localStorage.setItem('cookieConsent', 'declined');
        applyGtagConsent(false);
        logConsent(wasAccepted ? 'withdraw' : 'reject_all', false, language);
        setIsVisible(false);
        setTimeout(() => setShowConsent(false), 300);
        if (wasAccepted) {
            // Downgrade: purge existing cookies + reload so loaded scripts stop.
            purgeTrackingCookies();
            window.location.reload();
        }
    };

    if (!showConsent) {
        return null;
    }

    return (
        <div className={`cookie-consent-bar ${isVisible ? 'visible' : ''}`}>
            <div className="cookie-consent-content">
                <div className="cookie-icon">
                    <FaCookie />
                </div>
                <div className="cookie-text">
                    <h6 className="cookie-title">{Features.COOKIE_CONSENT.TITLE[language]}</h6>
                    <p className="cookie-message">
                        {Features.COOKIE_CONSENT.MESSAGE[language]}{' '}
                        <Link to="/politica-de-privacidade" className="cookie-link">
                            {Features.COOKIE_CONSENT.LEARN_MORE[language]}
                        </Link>
                    </p>
                    {localStorage.getItem('cookieConsent') && (
                        <p className="cookie-message" style={{ fontWeight: 600 }}>
                            {language === 'en' ? 'Current choice: ' : 'Escolha atual: '}
                            {localStorage.getItem('cookieConsent') === 'accepted'
                                ? (language === 'en' ? 'Cookies accepted' : 'Cookies aceites')
                                : (language === 'en' ? 'Cookies declined' : 'Cookies recusados')}
                        </p>
                    )}
                </div>
                <div className="cookie-actions">
                    <button 
                        onClick={handleDecline}
                        className="cookie-btn cookie-btn-decline"
                    >
                        {Features.COOKIE_CONSENT.DECLINE[language]}
                    </button>
                    <button 
                        onClick={handleAccept}
                        className="cookie-btn cookie-btn-accept"
                    >
                        {Features.COOKIE_CONSENT.ACCEPT[language]}
                    </button>
                </div>
            </div>
            <style jsx>{`
                .cookie-consent-bar {
                    position: fixed;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    background: var(--color-white);
                    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
                    z-index: 999998;
                    padding: 20px;
                    transform: translateY(100%);
                    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                }
                
                .cookie-consent-bar.visible {
                    transform: translateY(0);
                }
                
                .cookie-consent-content {
                    max-width: 1200px;
                    margin: 0 auto;
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    flex-wrap: wrap;
                }
                
                .cookie-icon {
                    font-size: 32px;
                    color: var(--color-primary);
                    flex-shrink: 0;
                }
                
                .cookie-text {
                    flex: 1;
                    min-width: 250px;
                }
                
                .cookie-title {
                    font-size: 18px;
                    font-weight: 600;
                    color: var(--color-dark);
                    margin: 0 0 8px 0;
                    font-family: var(--font-primary);
                }
                
                .cookie-message {
                    font-size: 14px;
                    color: var(--color-body);
                    margin: 0;
                    line-height: 1.6;
                    font-family: var(--font-primary);
                }
                
                .cookie-link {
                    color: var(--color-primary);
                    text-decoration: underline;
                    font-weight: 500;
                    transition: var(--transition);
                }
                
                .cookie-link:hover {
                    color: var(--color-link);
                }
                
                .cookie-actions {
                    display: flex;
                    gap: 12px;
                    flex-shrink: 0;
                }
                
                .cookie-btn {
                    padding: 12px 24px;
                    border-radius: 20px;
                    font-size: 14px;
                    font-weight: 500;
                    font-family: var(--font-primary);
                    cursor: pointer;
                    transition: var(--transition);
                    border: none;
                    white-space: nowrap;
                }
                
                .cookie-btn-decline {
                    background: transparent;
                    color: var(--color-body);
                    border: 1px solid var(--color-mercury);
                }
                
                .cookie-btn-decline:hover {
                    background: var(--color-light);
                    border-color: var(--color-gray-2);
                }
                
                .cookie-btn-accept {
                    background: var(--color-primary);
                    color: var(--color-white);
                    border: 1px solid var(--color-primary);
                }
                
                .cookie-btn-accept:hover {
                    background: var(--color-link);
                    border-color: var(--color-link);
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(89, 86, 233, 0.3);
                }
                
                @media (max-width: 767px) {
                    .cookie-consent-content {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 15px;
                    }
                    
                    .cookie-icon {
                        font-size: 28px;
                    }
                    
                    .cookie-text {
                        min-width: 100%;
                    }
                    
                    .cookie-actions {
                        width: 100%;
                        flex-direction: column;
                    }
                    
                    .cookie-btn {
                        width: 100%;
                    }
                }
                
                :global(body.active-dark-mode .cookie-consent-bar) {
                    background: var(--color-dark);
                    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.5);
                }
                
                :global(body.active-dark-mode .cookie-title) {
                    color: var(--color-white);
                }
                
                :global(body.active-dark-mode .cookie-message) {
                    color: var(--dark-body-text);
                }
                
                :global(body.active-dark-mode .cookie-btn-decline) {
                    background: rgba(255, 255, 255, 0.1);
                    color: var(--dark-body-text);
                    border-color: rgba(255, 255, 255, 0.2);
                }
                
                :global(body.active-dark-mode .cookie-btn-decline:hover) {
                    background: rgba(255, 255, 255, 0.15);
                }
            `}</style>
        </div>
    );
};

export default CookieConsent;

